import { clockTime } from './shiftWindow';
import type { DocNode } from '../notes/noteSerialize';

/**
 * The shift, written out.
 *
 * Hanneza's brief asked for "plain text, easily copy-pasteable" with timestamps
 * in her local time, and for empty categories to be left out entirely. All
 * three are about the same thing: what she pastes into Hubstaff should need no
 * editing, and a heading with nothing under it is something to delete.
 *
 * ── Two shapes, one source ─────────────────────────────────────────────────
 *
 * The Copy button hands over text. Save to My Notes has to hand over
 * ProseMirror JSON, because that is what user_notes.content holds — a plain
 * string there renders as one long unbroken line. Both are built from the same
 * grouped rows, so the note and the clipboard can never say different things.
 */

export interface ShiftRow {
  happened_at: string;
  category: string;
  summary: string;
  route: string;
}

/**
 * The order categories are read in, which is not the order they arrive in.
 *
 * Roughly: the person's own tasks and deals first, because since 20270119000100
 * the digest is "everything I did" and those are what an EOD opens with; then
 * outreach, which is most of a sales shift; then orders and the paperwork
 * around them; then the studios and reviews; then the quiet admin at the end.
 * Replies received sit after the outreach they answer. Anything the database
 * learns to return later that is not on this list still appears — at the end,
 * rather than not at all.
 */
const CATEGORY_ORDER = [
  'Tasks',
  'Deals',
  'Leads',
  'Emails',
  'Round touches',
  'Lead activity',
  'Replies received',
  'Companies',
  'Sales tasks',
  'LinkedIn',
  'Orders',
  'Order stages',
  'POs',
  'Files',
  'Copy Library',
  'Supplier reviews',
  'Inventory',
  'Comments',
  'Projects',
  'Social',
  'Content',
  'Leave',
  'Notes',
  'Admin',
];

export interface DigestGroup {
  category: string;
  lines: { time: string; text: string }[];
}

/** The rows, grouped and ordered, with empty categories already gone. */
export function groupShift(rows: ShiftRow[], timeZone: string): DigestGroup[] {
  const byCategory = new Map<string, { time: string; text: string }[]>();
  for (const row of rows) {
    const when = new Date(row.happened_at);
    const line = {
      time: Number.isNaN(when.getTime()) ? '' : clockTime(when, timeZone),
      text: row.summary,
    };
    const existing = byCategory.get(row.category);
    if (existing === undefined) byCategory.set(row.category, [line]);
    else existing.push(line);
  }
  const rank = (name: string) => {
    const at = CATEGORY_ORDER.indexOf(name);
    return at === -1 ? CATEGORY_ORDER.length : at;
  };
  return [...byCategory.entries()]
    .map(([category, lines]) => ({ category, lines }))
    .sort((a, b) => rank(a.category) - rank(b.category) || a.category.localeCompare(b.category));
}

/**
 * The digest as text.
 *
 * `title` is the shift, not the day it was generated — "Thu, Sep 3 · 8:00 PM to
 * 4:00 AM" is what she is reporting on, and the run date is noise.
 *
 * A shift with nothing in it says so in one line. An empty string would look
 * like the feature failed, and "nothing recorded" is a true and occasionally
 * useful sentence.
 */
export function digestText(title: string, groups: DigestGroup[]): string {
  if (groups.length === 0) {
    return `${title}\n\nNothing recorded in the Hub for this shift.`;
  }
  const body = groups
    .map((group) => {
      const lines = group.lines.map((line) => `  ${line.time}  ${line.text}`).join('\n');
      return `${group.category} (${String(group.lines.length)})\n${lines}`;
    })
    .join('\n\n');
  return `${title}\n\n${body}`;
}

function paragraph(text: string, bold = false): DocNode {
  return {
    type: 'paragraph',
    content:
      text === '' ? [] : [{ type: 'text', text, ...(bold ? { marks: [{ type: 'bold' }] } : {}) }],
  };
}

/**
 * The same digest as a note.
 *
 * Headings rather than bold paragraphs would nest under the note's own title
 * oddly and are harder to strip when she pastes onward; a bold line and a
 * bulleted list is what the rest of My Notes already looks like.
 */
export function digestDoc(title: string, groups: DigestGroup[]): DocNode {
  const content: DocNode[] = [paragraph(title, true)];
  if (groups.length === 0) {
    content.push(paragraph('Nothing recorded in the Hub for this shift.'));
    return { type: 'doc', content };
  }
  for (const group of groups) {
    content.push(paragraph(`${group.category} (${String(group.lines.length)})`, true));
    content.push({
      type: 'bulletList',
      content: group.lines.map((line) => ({
        type: 'listItem',
        content: [paragraph(`${line.time}  ${line.text}`)],
      })),
    });
  }
  return { type: 'doc', content };
}

/** The note's title. Short enough to read in the notes list at a glance. */
export function digestNoteTitle(title: string): string {
  return `Shift — ${title}`;
}
